// render-world-timeline.ts — the "catalog-world-timeline" renderer: a world's history as era
// bands running down the page with one lane per track (owner ruling §6.3, 2026-08-20).
//
// Drives world_timeline.php. The endpoint (options/api/world_timeline_inc.php) ships pure,
// ALREADY-ORDERED data; this file owns every pixel.
//
// ⛔ NEVER RE-SORT THE ENTRIES. They arrive in true chronological order, computed server-side from
//    the signed sort key that makes "300 BT → 0 BT → 432 AT" work (see
//    includes/functions/db/timeline_events/ft_timeline_date_inc.php). A JS
//    `.sort((a, b) => a.start.year - b.start.year)` looks harmless and silently reverses the whole
//    BT half of the chronology, because BT counts DOWN. Filtering toggles visibility; it never
//    reorders. Same discipline as the card renderers: build once, then show/hide.
//
// LAYOUT MODEL
//   The page is one vertical time axis. Entries flow down it in order. An era (kind='era') paints
//   a full-width band header wherever the chronology enters it, so the Ages read as chapters. Each
//   entry sits in the lane of the track(s) it belongs to — an entry on four tracks paints one row
//   with four connected nodes, which is how "THE GREAT WAR — [world], Orrin, Vagrax, Lyela" stays
//   a single event rather than four copies that drift apart.
//
//   A GAP marker is drawn where consecutive entries are far apart in years, because Zin's 131-year
//   silence at 159–290 AT is genuine content — it is where the Gods left — and a chronology that
//   renders it as an ordinary row-to-row step tells the reader the opposite of the truth.

import { worldBookGateCard } from './world-book-gate';
import type { CatalogListConfig } from './types';
import type {
    CatalogWorldTimelineResponse,
    WorldTimelineEntry,
    WorldTimelineTrack,
    WorldTimelineMarker,
} from './types';
import { initTooltips, resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';
import { parseText, ftMountParseTextTables } from '../shared/parse-text';

// Bootstrap's Offcanvas, used for the two author editors. Ambient because the bundle has no
// Bootstrap types; every call site guards on its presence so a page without Bootstrap JS degrades
// to "the editor doesn't open" rather than throwing.
declare const bootstrap: any;

// A gap this wide (in years) between consecutive entries earns a visible break marker.
// ⚠ Lowered 40 → 25 on 2026-08-23. Measured against the real chronology
// (tools/probes/zin_timeline_density_profile.php): Zin has ELEVEN empty stretches of 25+ years, and
// a threshold of 40 drew a marker for only FOUR of them. The other seven rendered as unexplained
// dead scroll — the reader could not tell a 30-year silence from a tight run of entries.
const GAP_YEARS = 25;

// A year holding this many entries collapses to a summary row (owner ruling, 2026-08-23).
// ⚠ Measured, not guessed: 431 AT holds 20 events — 26% of the whole chronology in one year — and
// 100 AT holds 8, 0 AT holds 6. A threshold of 5 folds exactly those three knots and leaves the
// other 35 populated years untouched.
const CLUSTER_MIN = 5;
// How many entries stay visible above the "N more" toggle.
const CLUSTER_PREVIEW = 3;

interface BuiltEntry {
    el: HTMLElement;
    text: string;
    entry: WorldTimelineEntry;
}

function icon(id: string, w = 20, h = 20, title: string | boolean = false): string {
    return typeof iconMap === 'function' ? iconMap(id, w, h, title) : '';
}

function el(tag: string, className = '', text = ''): HTMLElement {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
}

// ── Track colour ────────────────────────────────────────────────────────────────────────────
// The endpoint whitelists timeline_colour to #rrggbb, so it is safe as a custom property. Falls
// back to a deterministic hue per lane so an author who never picks colours still gets legible,
// stable lanes (stable matters: a colour that changes between loads reads as a different track).
function trackColour(track: WorldTimelineTrack, index: number): string {
    if (/^#[0-9a-fA-F]{6}$/.test(track.colour || '')) {
        return track.colour;
    }
    return `hsl(${(index * 67) % 360} 70% 60%)`;
}

// ── Track tree ──────────────────────────────────────────────────────────────────────────────
// Tracks NEST — Stormbrook under Orrin under Zin — and a parent can be COLLAPSED.
//
// ⭐ A COLLAPSED PARENT LOSES NOTHING. Its lane paints a node for any entry filed on it OR on
// anything beneath it, so a Stormbrook event stays visible while Orrin is folded; expanding merely
// reveals WHICH child it belongs to. That is the whole reason the endpoint ships `descendant_ids`.
// The alternative — hiding a collapsed parent's children — makes real content invisible with no
// hint it exists, the same failure the Age sections were restructured to avoid.

// Depth-first order with collapsed subtrees pruned. The result IS the lane order, left to right.
function visibleTracks(tracks: WorldTimelineTrack[], collapsed: Set<string>): WorldTimelineTrack[] {
    const byParent = new Map<string, WorldTimelineTrack[]>();
    for (const t of tracks) {
        const key = t.parent_id || '';
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key)!.push(t);
    }
    for (const list of byParent.values()) list.sort((a, b) => a.sort_order - b.sort_order);

    const out: WorldTimelineTrack[] = [];
    const walk = (parentId: string, hops: number): void => {
        if (hops > 8) return;                 // matches the server's cap; a cycle cannot spin here
        for (const t of byParent.get(parentId) || []) {
            out.push(t);
            if (!collapsed.has(t.timeline_id)) walk(t.timeline_id, hops + 1);
        }
    };
    walk('', 0);

    // Orphans: a track whose parent is filtered out by privacy would otherwise never be walked.
    // Losing a lane silently is worse than showing it at the root, so sweep them in.
    const placed = new Set(out.map((t) => t.timeline_id));
    for (const t of tracks) {
        if (!placed.has(t.timeline_id) && !isInsideCollapsed(t, tracks, collapsed)) out.push(t);
    }
    return out;
}

// Is this track underneath something currently collapsed? Used only by the orphan sweep, so a
// genuinely-folded child is not re-added at the root.
function isInsideCollapsed(track: WorldTimelineTrack, tracks: WorldTimelineTrack[], collapsed: Set<string>): boolean {
    const byId = new Map(tracks.map((t) => [t.timeline_id, t]));
    let cursor = track.parent_id;
    for (let hop = 0; hop < 8 && cursor; hop++) {
        if (collapsed.has(cursor)) return true;
        cursor = byId.get(cursor)?.parent_id || '';
    }
    return false;
}

// The chip carries up to two actions, so the tooltip is the only place either one is spelled
// out. A control whose second action is undiscoverable is a control that does not exist.
function trackChipTitle(track: WorldTimelineTrack, isCollapsed: boolean, canAuthor: boolean): string {
    const kids = track.descendant_ids.length;
    if (!kids) return canAuthor ? `Edit ${track.name}` : track.name;
    // Kept SHORT. Bootstrap sizes a tooltip to its text, and a sentence-long one hanging off a
    // 16px-wide chip covers the whole track strip it is trying to explain.
    const fold = isCollapsed
        ? `Show ${kids} track${kids === 1 ? '' : 's'} under ${track.name}`
        : `Fold ${track.name} — children roll up here`;
    return canAuthor ? `${fold} · dbl-click to edit` : fold;
}

// ── Nodes ───────────────────────────────────────────────────────────────────────────────────
// One entry's lane row: a cell per VISIBLE track, with a node in each lane the entry belongs to and
// a connector across the span between its first and last. That connector is what makes a
// multi-continent event read as ONE event at a glance.
function buildLaneRow(entry: WorldTimelineEntry, tracks: WorldTimelineTrack[], collapsed: Set<string>): HTMLElement {
    const lanes = el('div', 'ft-timeline-lanes');
    lanes.style.setProperty('--ft-lane-count', String(Math.max(1, tracks.length)));

    const memberIndexes: number[] = [];
    tracks.forEach((track, i) => {
        // ⭐ ROLL-UP ONLY WHILE FOLDED. An EXPANDED parent claims only what is filed directly on
        // it — its children have their own lanes, so echoing every child event onto the trunk as
        // well would fill Zin's lane with dozens of hollow nodes saying nothing the child lanes
        // do not already say. A FOLDED parent claims "self or below", which is what keeps the
        // event on the page at all.
        const own = entry.timeline_ids.includes(track.timeline_id);
        const rolled = collapsed.has(track.timeline_id)
            && !own
            && track.descendant_ids.some((id) => entry.timeline_ids.includes(id));
        if (own || rolled) memberIndexes.push(i);
    });

    // An entry with no membership still deserves a node — an author who has not filed it under a
    // track yet must be able to see it, or it becomes invisible content they cannot fix.
    const effective = memberIndexes.length ? memberIndexes : [0];

    const first = effective[0];
    const last = effective[effective.length - 1];
    if (effective.length > 1 && first !== undefined && last !== undefined) {
        const connector = el('div', 'ft-timeline-connector');
        connector.style.gridColumn = `${first + 1} / ${last + 2}`;
        lanes.appendChild(connector);
    }

    tracks.forEach((track, i) => {
        const cell = el('div', 'ft-timeline-lane');
        cell.style.gridColumn = String(i + 1);
        if (effective.includes(i)) {
            const node = el('span', 'ft-timeline-node');
            node.style.setProperty('--ft-track-colour', trackColour(track, i));
            // A rolled-up node is drawn HOLLOW, so a folded parent never looks like it owns an
            // event it is only carrying — the reader sees the detail is one level down.
            const ownsIt = entry.timeline_ids.includes(track.timeline_id);
            if (!ownsIt) node.classList.add('ft-timeline-node--rolled');
            node.setAttribute('data-bs-toggle', 'tooltip');
            node.setAttribute(
                'data-bs-original-title',
                ownsIt ? track.name : `${track.name} — nested, expand to see which`,
            );
            cell.appendChild(node);
        }
        lanes.appendChild(cell);
    });

    return lanes;
}

function buildEntryRow(
    entry: WorldTimelineEntry,
    tracks: WorldTimelineTrack[],
    canAuthor: boolean,
    onEdit: (entry: WorldTimelineEntry) => void,
    collapsed: Set<string>,
): HTMLElement {
    const row = el('div', 'ft-timeline-row');
    if (entry.derived) row.classList.add('ft-timeline-row--derived');
    if (entry.is_private > 0) row.classList.add('ft-timeline-row--private');

    // DATE gutter — the ready-made label from PHP. Formatting lives server-side so the era
    // notation has exactly one implementation and TS can never drift from it.
    const date = el('div', 'ft-timeline-date');
    date.appendChild(el('span', 'ft-timeline-date-label', entry.label || '—'));
    row.appendChild(date);

    row.appendChild(buildLaneRow(entry, tracks, collapsed));

    // ── The CATEGORY MEDALLION (World Anvil's signature) ────────────────────────────────────────
    // A circular, ringed mark that says at a glance what KIND of thing happened — war, a founding,
    // a death, something divine — before a word of the entry is read. The ring colour groups the
    // taxonomy (red conflict, amber rule, violet the numinous, green making). See
    // includes/functions/db/timeline_events/ft_timeline_category_inc.php.
    //
    // ⭐ IT IS ITS OWN GRID COLUMN, not the first child of the title line (owner correction
    // 2026-08-21). Inline, the medallion pushed the TITLE right while the description underneath
    // stayed at the body's left edge — so every entry's heading was indented relative to its own
    // text, and the medallions never formed a clean vertical strip. As a column the title and its
    // description share one left edge and the marks line up down the page.
    const medallion = el('span', 'ft-timeline-medallion');
    medallion.style.setProperty('--ft-cat-colour', entry.category_colour || '#8fa3b8');
    medallion.innerHTML = icon(entry.category_icon || 'points_of_interest', 27, 27, false);
    medallion.setAttribute('data-bs-toggle', 'tooltip');
    medallion.setAttribute('data-bs-original-title', entry.category_label || 'Event');
    const medallionCell = el('div', 'ft-timeline-medallion-cell');
    medallionCell.appendChild(medallion);
    row.appendChild(medallionCell);

    const body = el('div', 'ft-timeline-body');

    const head = el('div', 'ft-timeline-head');

    const title = el('h3', 'ft-timeline-title', entry.name);
    head.appendChild(title);
    if (entry.is_private === 1) {
        head.appendChild(el('span', 'badge bg-warning text-dark ft-timeline-badge', 'private'));
    }
    if (entry.is_private === 2) {
        head.appendChild(el('span', 'badge bg-danger ft-timeline-badge', 'secret'));
    }
    // ⭐ CANON TIER chip (owner, 2026-08-23). Campaign-driven entries stay on the shared chronology
    // but are HIGHLIGHTED; the campaign's name appears only when the endpoint chose to ship it
    // (viewer has standing in that campaign, or authors the world) — otherwise the chip reads
    // "campaign" and nothing rots when that campaign is later deleted.
    if (entry.canon_tier === 'campaign') {
        row.classList.add('ft-timeline-row--campaign');
        const chip = el('span', 'badge ft-timeline-badge ft-timeline-badge--campaign', entry.campaign_name ? `campaign · ${entry.campaign_name}` : 'campaign');
        chip.setAttribute('data-bs-toggle', 'tooltip');
        chip.setAttribute('data-bs-original-title', "Campaign-driven: this happened at a table, not in the world's bedrock history.");
        head.appendChild(chip);
    } else if (entry.canon_tier === 'bedrock') {
        row.classList.add('ft-timeline-row--bedrock');
    }
    // A projected location date. Badged so an author knows why it isn't editable here and where
    // it actually lives — silent read-only fields are how people conclude a feature is broken.
    if (entry.derived) {
        const badge = el('span', 'badge bg-info text-dark ft-timeline-badge', 'from location');
        badge.setAttribute('data-bs-toggle', 'tooltip');
        badge.setAttribute('data-bs-original-title', 'Recorded on the location itself. Edit it there, or click to make it a full timeline entry.');
        head.appendChild(badge);
    }

    if (canAuthor) {
        const edit = document.createElement('button');
        edit.type = 'button';
        edit.className = 'btn btn-sm btn-outline-secondary ft-timeline-edit';
        edit.setAttribute('aria-label', entry.derived ? 'Promote to a full entry' : 'Edit entry');
        edit.innerHTML = icon('modify', 16, 16, false) || '✎';
        edit.addEventListener('click', () => onEdit(entry));
        head.appendChild(edit);
    }

    body.appendChild(head);

    if (entry.description) {
        // The description is Quill rich text, sanitised through HTMLPurifier on the way in
        // (mysql_safe_ajax with $allow_html), which is the same trust boundary every other
        // rich-text surface on the site uses.
        //
        // ⭐ Run it through parseText so timeline entries carry the same rich links every other
        // surface does — [Aspis](article:<uuid>), [Ikos](location:<uuid>), [Soulreaver](item:<uuid>).
        // Until 2026-08-23 this assigned innerHTML raw, so a tag rendered as literal
        // "[Aspis](article:…)" text. parseText wraps its output in .textarea-output and is the same
        // call the log, object-card and PC-sheet surfaces make.
        const text = el('div', 'ft-timeline-text');
        text.innerHTML = parseText(entry.sampled ? `${entry.description.replace(/[.…]+$/u, '')}…` : entry.description);
        body.appendChild(text);
        ftMountParseTextTables(text);
    }

    if (entry.gm_notes) {
        const notes = el('div', 'ft-timeline-gm-notes');
        notes.appendChild(el('span', 'ft-timeline-gm-label', 'GM'));
        notes.appendChild(el('span', '', entry.gm_notes));
        body.appendChild(notes);
    }

    // Which continents this touched, spelled out — the lane nodes carry it visually, but the
    // names are what a reader actually wants on a multi-continent event.
    if (entry.timeline_ids.length > 1) {
        const on = el('div', 'ft-timeline-tracks-note');
        const names = tracks
            .filter((t) => entry.timeline_ids.includes(t.timeline_id))
            .map((t) => t.name);
        on.textContent = names.join(' · ');
        body.appendChild(on);
    }

    row.appendChild(body);
    return row;
}

// ── Age header ──────────────────────────────────────────────────────────────────────────────
// The header of an Age <section>. A real <button> so it is keyboard-reachable and screen-reader
// announced: clicking COLLAPSES the Age. Seven Ages of a 46-entry chronology is a lot of page, and
// being able to fold the ones you are not reading is the difference between a reference and a wall.
function buildEraBand(
    era: WorldTimelineEntry,
    body: HTMLElement,
    canAuthor: boolean,
    onEdit: (entry: WorldTimelineEntry, kind?: string) => void,
): HTMLElement {
    const band = el('div', 'ft-timeline-era');
    const inner = el('div', 'ft-timeline-era-inner');

    // ⭐ THE BANNER. Painted as a background layer behind the band rather than as an <img> above
    // it, so the title, range and description keep sitting on the band itself — an image row would
    // push the Age header a screenful down the page and break the rhythm the bands exist to give.
    // The url is already resolved and world-scoped by the endpoint; '' means no banner.
    if (era.banner_url) {
        band.classList.add('ft-timeline-era--bannered');
        const art = el('div', 'ft-timeline-era-banner');
        // ⚠ Set as a style property, not interpolated into innerHTML: the url comes from the
        // pictures table, and url(...) is a place where a stray quote would be an injection.
        art.style.backgroundImage = `url("${era.banner_url.replace(/["'\\()]/g, '')}")`;
        band.appendChild(art);
    }

    // An Age carries its own medallion, so the headers and the entries read as one system rather
    // than two unrelated row types.
    const titleRow = el('div', 'ft-timeline-era-head');

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'ft-timeline-era-toggle';
    toggle.setAttribute('aria-expanded', 'true');

    const medallion = el('span', 'ft-timeline-medallion ft-timeline-medallion--era');
    medallion.style.setProperty('--ft-cat-colour', era.category_colour || '#8e7bd6');
    medallion.innerHTML = icon(era.category_icon || 'calendar', 32, 32, false);
    toggle.appendChild(medallion);

    const heading = el('span', 'ft-timeline-era-headings');
    heading.appendChild(el('span', 'ft-timeline-era-title', era.name));
    const meta = el('span', 'ft-timeline-era-meta');
    meta.appendChild(el('span', 'ft-timeline-era-range', era.label));
    heading.appendChild(meta);
    toggle.appendChild(heading);

    toggle.appendChild(el('span', 'ft-timeline-era-chevron', '▾'));

    toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        band.classList.toggle('ft-timeline-era--collapsed', open);
        body.style.display = open ? 'none' : '';
    });

    titleRow.appendChild(toggle);

    // ⭐ AGES ARE EDITABLE (owner ask 2026-08-21). The editor already spoke kind='era' — the "Add
    // age" button has used it since day one — so an Age could be CREATED but never corrected: a
    // typo in "Age of Mortals" was permanent short of a probe. ⚠ Outside the toggle <button>, not
    // inside it: nesting a button in a button is invalid HTML and the click would toggle the fold
    // on its way to the pencil.
    if (canAuthor) {
        const edit = document.createElement('button');
        edit.type = 'button';
        edit.className = 'btn btn-sm btn-outline-secondary ft-timeline-edit ft-timeline-era-edit';
        edit.setAttribute('aria-label', `Edit ${era.name}`);
        // Exposed for the smoke: it needs a real Age id to prove the media manager's guard.
        edit.setAttribute('data-ft-era-id', era.event_id);
        edit.setAttribute('data-bs-toggle', 'tooltip');
        edit.setAttribute('data-bs-original-title', 'Edit this age');
        edit.innerHTML = icon('modify', 16, 16, false) || '✎';
        edit.addEventListener('click', (e) => {
            e.stopPropagation();
            onEdit(era, 'era');
        });
        titleRow.appendChild(edit);
    }

    inner.appendChild(titleRow);

    if (era.description) {
        // Same treatment as an entry description — an era band is authored in the same editor and
        // wants the same links (the Age of Beginnings naming Aspis and Deraëe is the motivating case).
        const text = el('div', 'ft-timeline-era-text');
        text.innerHTML = parseText(era.sampled ? `${era.description.replace(/[.…]+$/u, '')}…` : era.description);
        inner.appendChild(text);
        ftMountParseTextTables(text);
    }
    band.appendChild(inner);
    return band;
}

// ── Gap marker ──────────────────────────────────────────────────────────────────────────────
// Drawn between two entries separated by more than GAP_YEARS. Uses the SIGNED sort years, so the
// distance is correct across the BT/AT pivot — 31 BT to 5 AT is 36 years, not 26.
function buildGapMarker(fromYear: number, toYear: number): HTMLElement {
    const gap = el('div', 'ft-timeline-gap');
    const years = toYear - fromYear;
    gap.appendChild(el('span', 'ft-timeline-gap-label', `${years} years, nothing recorded`));
    return gap;
}

// ── Year clustering ─────────────────────────────────────────────────────────────────────────
// A single year holding CLUSTER_MIN+ entries collapses: CLUSTER_PREVIEW rows stay, the rest hide
// behind a toggle. ⛔ The rows are BUILT either way and only hidden — search has to be able to find
// what is inside a collapsed cluster, and the filter walks `built[]`, not the DOM. A cluster that
// dropped its rows would make its contents unsearchable.
function buildClusterToggle(label: string, hiddenCount: number, rows: HTMLElement[]): HTMLElement {
    const toggle = el('button', 'ft-timeline-cluster-toggle');
    toggle.setAttribute('type', 'button');
    toggle.setAttribute('aria-expanded', 'false');

    const setLabel = (open: boolean): void => {
        toggle.textContent = open ? `Show less — ${label}` : `${hiddenCount} more in ${label}`;
    };
    setLabel(false);

    toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') === 'true';
        rows.forEach((r) => { r.classList.toggle('ft-timeline-clustered-hidden', open); });
        toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        setLabel(!open);
    });
    return toggle;
}

// ── The Age rail ────────────────────────────────────────────────────────────────────────────
// A persistent left-hand strip showing where you are in the WHOLE chronology, and letting you jump.
//
// ⭐ IT IS DOCUMENT-PROPORTIONAL, NOT TIME-PROPORTIONAL, and that is a deliberate choice.
// Scroll position in this page is not proportional to time and never can be: gap compression
// squeezes 136 empty years into one rule, and year clustering folds 20 events into 3 rows. A rail
// scaled to TIME would show the true shape of history but its thumb would lie about where you are —
// jumping halfway down the rail would land you somewhere else entirely. A rail scaled to the
// DOCUMENT tells the truth about position and makes clicking predictable, at the cost of the Ages
// appearing sized by how much was written rather than how long they lasted. For a NAVIGATION
// control, truthful position wins; the era bands in the page body already carry the real durations.
type AgeRailSeg = { section: HTMLElement; label: string; count: number; btn: HTMLElement };

function buildAgeRail(
    container: HTMLElement,
    ageSections: Array<{ era: any; section: HTMLElement; body: HTMLElement; rows: HTMLElement[] }>,
): HTMLElement | null {
    const usable = ageSections.filter((r) => r.era && r.section.isConnected);
    if (usable.length < 2) { return null; }   // one Age needs no index

    const rail = el('nav', 'ft-timeline-rail');
    rail.setAttribute('aria-label', 'Ages of this history');

    const track = el('div', 'ft-timeline-rail-track');
    rail.appendChild(track);

    const thumb = el('div', 'ft-timeline-rail-thumb');
    thumb.setAttribute('aria-hidden', 'true');
    track.appendChild(thumb);

    const segs: AgeRailSeg[] = usable.map((rec) => {
        const label = (rec.era.name || 'Age').toString();
        const btn = el('button', 'ft-timeline-rail-seg');
        btn.setAttribute('type', 'button');
        btn.title = `${label} — ${rec.rows.length} ${rec.rows.length === 1 ? 'event' : 'events'}`;

        const bar = el('span', 'ft-timeline-rail-bar');
        bar.setAttribute('aria-hidden', 'true');
        btn.appendChild(bar);

        const text = el('span', 'ft-timeline-rail-label');
        text.appendChild(el('span', 'ft-timeline-rail-name', label));
        text.appendChild(el('span', 'ft-timeline-rail-count', String(rec.rows.length)));
        btn.appendChild(text);

        // ⚠ scrollIntoView, not a manual scrollTop sum — the page has sticky chrome above it and
        // the browser already knows how to account for scroll-margin.
        // ⛔ Jumping must MOVE FOCUS to the destination heading, or a screen-reader user is scrolled
        // somewhere they are never told about. Passive scrolling never moves focus; activating a
        // navigation control always should.
        btn.addEventListener('click', () => {
            rec.section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            const heading = rec.section.querySelector<HTMLElement>('.ft-timeline-era-title')
                || rec.section.querySelector<HTMLElement>('h2, h3')
                || rec.section;
            if (!heading.hasAttribute('tabindex')) { heading.setAttribute('tabindex', '-1'); }
            heading.focus({ preventScroll: true });
        });
        track.appendChild(btn);
        return { section: rec.section, label, count: rec.rows.length, btn };
    });

    // ── Measure ──────────────────────────────────────────────────────────────────────────────
    // ⚠ Recomputed rather than cached: Age sections collapse and year clusters unfold, so document
    // height is not stable. A ResizeObserver on the container catches every one of those without
    // each toggle having to remember to tell the rail.
    const measure = (): void => {
        const total = container.offsetHeight || 1;
        segs.forEach((s) => {
            const share = (s.section.offsetHeight || 0) / total;
            // A floor so an empty Age is still a clickable target rather than a 0px sliver.
            s.btn.style.flexGrow = String(Math.max(share, 0.02));
        });
    };

    // ── Scrollspy + thumb ────────────────────────────────────────────────────────────────────
    // ⚠ The READING LINE is at 25% down the viewport, not the midpoint. A reader is looking near the
    // top of what they just scrolled to, so a midpoint test marks the NEXT Age current while its
    // heading is still below the fold. 20–30% is the range that tracks where attention actually is.
    const READING_LINE = 0.25;

    const update = (): void => {
        const viewport = window.innerHeight || 1;
        const line = viewport * READING_LINE;

        // The current Age is the last one whose top edge is above the reading line.
        let activeIdx = 0;
        segs.forEach((s, i) => {
            if (s.section.getBoundingClientRect().top <= line) { activeIdx = i; }
        });

        // ⭐ The thumb is placed FROM THE ACTIVE SEGMENT, not from a raw document percentage.
        // The track puts a 3px gap between segments — ~18px across seven Ages — which a pure
        // percentage does not account for, so the dot drifted off the very segment it was meant to
        // be marking. Interpolating inside the active segment's own box makes the dot always rest
        // on the highlighted Age, which is the guarantee that matters to a reader.
        const active = segs[activeIdx];
        if (!active) { return; }
        const box = active.section.getBoundingClientRect();
        const throughSection = box.height > 0
            ? Math.min(1, Math.max(0, (line - box.top) / box.height))
            : 0;
        const segTop = active.btn.offsetTop;
        const segHeight = active.btn.offsetHeight || 1;
        thumb.style.top = `${segTop + throughSection * segHeight}px`;
        segs.forEach((s, i) => {
            s.btn.classList.toggle('is-current', i === activeIdx);
            if (i === activeIdx) { s.btn.setAttribute('aria-current', 'true'); }
            else { s.btn.removeAttribute('aria-current'); }
        });
    };

    let queued = false;
    const onScroll = (): void => {
        if (queued) { return; }
        queued = true;
        requestAnimationFrame(() => { queued = false; update(); });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    if (typeof ResizeObserver === 'function') {
        const ro = new ResizeObserver(() => { measure(); onScroll(); });
        ro.observe(container);
    }

    // First measure after layout has settled — offsetHeight is 0 while the tree is still being built.
    requestAnimationFrame(() => { measure(); update(); });

    return rail;
}

// Force a cluster open — used when a search hits a row inside it, so a match can never be invisible.
function revealCluster(row: HTMLElement): void {
    if (!row.classList.contains('ft-timeline-clustered-hidden')) { return; }
    const key = row.getAttribute('data-ft-cluster');
    if (!key) { row.classList.remove('ft-timeline-clustered-hidden'); return; }
    const scope = row.closest('.ft-timeline-section') || document;
    scope.querySelectorAll<HTMLElement>(`[data-ft-cluster="${key}"]`)
        .forEach((r) => r.classList.remove('ft-timeline-clustered-hidden'));
    const t = scope.querySelector<HTMLElement>(`[data-ft-cluster-toggle="${key}"]`);
    if (t) { t.setAttribute('aria-expanded', 'true'); t.textContent = 'Show less'; }
}

// Someone who has asked the OS for less motion has asked for it here too - a 99-entry smooth
// scroll is a long way to be dragged. The CSS already drops the landing flash; this drops the travel.
function prefersReducedMotion(): boolean {
    return typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ── Jump to year ────────────────────────────────────────────────────────────────────────────
// ⛔ THE ERA TRAP: Zin runs 300 BT → 0 BT → 432 AT, so a raw year is NOT sortable — 300 BT is
// EARLIER than 100 BT. PHP already solved this: `ft_timeline_sort_year()` emits a SIGNED year
// (BT negative, AT positive) onto every entry as `start_sort_year`, and the rows are rendered in
// that order. So parsing only has to reach the same signed number and the scan is a walk in
// document order — never a client-side re-sort.
// ⚠ A blank era means AT, matching ft_timeline_normalize_era(): "unmarked" is the present era in
// every dating system that has one.
function parseJumpYear(raw: string): number | null {
    // DO NOT strip punctuation globally before matching. An earlier cut did
    // `.replace(/\.|\s+/g, '')`, which silently welded digits together: "4.31 AT" and "4 31 AT" both
    // became 431 AT - a typo answered with confident nonsense. Whitespace is collapsed, never removed,
    // and the only dots tolerated are the ones inside "B.T." / "A.T.".
    const t = (raw || '').trim().toUpperCase().replace(/\s+/g, ' ');
    if (t === '') { return null; }
    const m = /^(-?)(\d+) ?(B\.?T\.?|A\.?T\.?)?$/.exec(t);
    if (!m) { return null; }
    const era = (m[3] || '').replace(/\./g, '');
    // "-100 AT" is a contradiction, not a typo to guess at: the sign says BT and the token says AT.
    // The old code resolved it to +100 - the opposite of one of the two things the user wrote.
    if (m[1] === '-' && era === 'AT') { return null; }
    const year = parseInt(m[2] ?? '', 10);
    // No arbitrary digit cap: an absurd year is not an error, it just falls off the end of recorded
    // history and the at-or-after scan lands on the last entry, which is the honest answer.
    if (!Number.isSafeInteger(year)) { return null; }
    return (era === 'BT' || m[1] === '-') ? -year : year;
}

// Land on the first entry AT OR AFTER the target — the typed year usually holds nothing (Zin has
// eleven gaps of 25 years or more), so an exact-match-only jump would fail far more often than it
// worked. Falls back to the LAST entry when the target is past the end of recorded history.
// ⛔ Must reveal a folded cluster and MOVE FOCUS, for exactly the reasons the Age rail does.
function jumpToYear(built: Array<{ el: HTMLElement; entry: WorldTimelineEntry }>, target: number): HTMLElement | null {
    // ONLY ROWS THE ACTIVE FILTERS LEAVE VISIBLE. A search or "Hide campaign events" sets
    // display:none on the rows it excludes, and scrollIntoView()/focus() on a display:none element
    // does nothing at all - the page would sit still and the jump would look broken. Landing on the
    // nearest VISIBLE entry is also what someone who has just filtered actually means.
    const usable = built.filter((b) => b.el.style.display !== 'none'
        && b.entry.start_sort_year !== null && b.entry.start_sort_year !== undefined);
    if (!usable.length) { return null; }

    let hit: HTMLElement | null = null;
    for (const b of usable) {
        if ((b.entry.start_sort_year as number) >= target) { hit = b.el; break; }
    }
    // Past the end of recorded history - land on the last thing there is.
    if (!hit) { hit = usable[usable.length - 1]?.el ?? null; }
    if (!hit) { return null; }
    revealCluster(hit);
    // An Age whose body is collapsed cannot be scrolled into — open it first.
    const body = hit.closest<HTMLElement>('.ft-timeline-age-body');
    if (body && body.hidden) { body.hidden = false; }
    hit.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    if (!hit.hasAttribute('tabindex')) { hit.setAttribute('tabindex', '-1'); }
    hit.focus({ preventScroll: true });
    hit.classList.add('ft-timeline-row--jumped');
    window.setTimeout(() => hit && hit.classList.remove('ft-timeline-row--jumped'), 1600);
    return hit;
}

// ── The campaign clock marker ───────────────────────────────────────────────────────────────
function buildMarker(marker: WorldTimelineMarker): HTMLElement {
    const row = el('div', 'ft-timeline-marker');
    const label = el('div', 'ft-timeline-marker-label');
    label.appendChild(el('strong', '', marker.name || 'Campaign'));
    label.appendChild(el('span', 'ft-timeline-marker-date', marker.label));
    if (!marker.calendar_matches_world) {
        const warn = el('span', 'badge bg-warning text-dark ms-2', 'different calendar');
        warn.setAttribute('data-bs-toggle', 'tooltip');
        warn.setAttribute('data-bs-original-title', 'This campaign runs on a different calendar from its world, so the alignment is approximate.');
        label.appendChild(warn);
    }
    row.appendChild(label);
    return row;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// Editors
// ══════════════════════════════════════════════════════════════════════════════════════════════

// ── Editor chrome ───────────────────────────────────────────────────────────────────────────
// Dress one offcanvas: accent hue, leading mark, title. The ACCENT is what makes the panel read as
// part of the thing being edited — an Age gets the violet its bands use, a track gets that track's
// own colour, an entry gets its category colour. The header wash and the icon ring both key off the
// same custom property, so one assignment tints the whole header.
//
// ⚠ Sets the LABEL span, not the title element's textContent — the title also holds the icon slot,
// and textContent would wipe it.
function dressEditor(panelId: string, accent: string, iconName: string, label: string): void {
    const panel = document.getElementById(panelId);
    if (!panel) return;
    panel.style.setProperty('--ft-editor-accent', accent);
    const mark = panel.querySelector<HTMLElement>('[data-ft-editor-icon]');
    if (mark) mark.innerHTML = iconName ? icon(iconName, 22, 22, false) : '';
    const text = panel.querySelector<HTMLElement>('[data-ft-editor-label]');
    if (text) text.textContent = label;
}

function formFields(form: HTMLElement): HTMLElement[] {
    return Array.from(form.querySelectorAll<HTMLElement>('[data-ft-field]'));
}

function readForm(form: HTMLElement): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const field of formFields(form)) {
        const name = field.getAttribute('data-ft-field') || '';
        if (!name) continue;
        if (field instanceof HTMLInputElement && field.type === 'checkbox') {
            out[name] = field.checked ? 1 : 0;
        } else if (field instanceof HTMLInputElement && field.type === 'time') {
            // <input type="time"> gives "HH:MM"; the column stores minute-of-day, which is what
            // the sort key folds in. Empty stays empty — "no time stated" is the common case.
            const raw = field.value;
            out[name] = raw ? Number(raw.slice(0, 2)) * 60 + Number(raw.slice(3, 5)) : '';
        } else {
            out[name] = (field as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value;
        }
    }
    return out;
}

function writeForm(form: HTMLElement, values: Record<string, unknown>): void {
    for (const field of formFields(form)) {
        const name = field.getAttribute('data-ft-field') || '';
        const value = values[name];
        if (field instanceof HTMLInputElement && field.type === 'checkbox') {
            field.checked = !!value;
        } else if (field instanceof HTMLInputElement && field.type === 'time') {
            const minutes = typeof value === 'number' ? value : null;
            field.value = minutes === null
                ? ''
                : `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
        } else {
            (field as HTMLInputElement).value = value === null || value === undefined ? '' : String(value);
        }
    }
    // Lets dependent UI (the canon-tier → campaign picker reveal) re-sync after a silent fill.
    form.dispatchEvent(new CustomEvent('ft:form-written'));
}

function showEditorError(form: HTMLElement, message: string): void {
    const box = form.querySelector<HTMLElement>('[data-ft-timeline-editor-error]');
    if (!box) return;
    box.textContent = message;
    box.style.display = message ? '' : 'none';
}

async function postJson(url: string, payload: Record<string, unknown>): Promise<any> {
    const res = await fetch(resolveUrl(url), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'same-origin',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.error) {
        throw new Error(data.error || `HTTP ${res.status}`);
    }
    return data;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// Mount
// ══════════════════════════════════════════════════════════════════════════════════════════════
export function mountCatalogWorldTimeline(root: HTMLElement, config: CatalogListConfig): void {
    const container = results(root);
    if (!container) return;

    const worldLocationId = root.getAttribute('data-ft-world-location-id') || '';
    const campaignId = root.getAttribute('data-ft-campaign-id') || '';
    const emptyText = root.getAttribute('data-ft-empty-text') || 'No history recorded yet.';
    const campaignToggle = document.getElementById('ft_wt_hide_campaign') as HTMLInputElement | null;
    const searchInput = config.searchInputId
        ? (document.getElementById(config.searchInputId) as HTMLInputElement | null)
        : null;

    if (!worldLocationId) {
        setStatus(root, 'No world specified.');
        return;
    }

    const load = (): void => {
        const loadingTimer = window.setTimeout(() => showLoader(root, config), 250);

        void (async () => {
            try {
                const params = new URLSearchParams({ world_location_id: worldLocationId });
                if (campaignId) params.set('campaign_id', campaignId);

                const res = await fetch(`${resolveUrl(config.endpoint)}?${params.toString()}`, {
                    credentials: 'same-origin',
                });
                const data: CatalogWorldTimelineResponse = await res.json().catch(() => ({} as any));
                if (!res.ok || (data as any).error) {
                    throw new Error((data as any).error || `HTTP ${res.status}`);
                }
                clearTimeout(loadingTimer);
                hideLoader(root);
                setStatus(root, '');
                build(data);
            } catch (e) {
                clearTimeout(loadingTimer);
                hideLoader(root);
                console.error('[catalog-world-timeline] fetch/render failed', e);
                setStatus(root, '');
                container.innerHTML = '';
                const msg = el('div', 'text-center text-muted py-4', 'Could not load this timeline. Please try again.');
                container.appendChild(msg);
            }
        })();
    };

    function build(data: CatalogWorldTimelineResponse): void {
        container!.innerHTML = '';
        if (data.gate?.gated && !data.gate.entitled) container!.append(worldBookGateCard(data.gate, data.world.name));

        // The lane set is the track tree walked depth-first with folded subtrees pruned — NOT a
        // flat sort. A collapsed parent still claims its children's events (see buildLaneRow), so
        // folding changes the level of detail, never what is on the page.
        const allTracks = data.tracks || [];
        seedDeepFolds(allTracks);

        // ── FOCUS (the eye) ─────────────────────────────────────────────────────────────────
        // Zoom into one track: its lane and its descendants' lanes stay, everything else goes,
        // and only entries filed on that subtree are rendered (owner ask 2026-08-21).
        //
        // ⭐ SUBTREE, not the single track. Focusing Orrin has to include Stormbrook and Drakovar,
        // or focusing a parent would show LESS than folding it — which makes no sense to a reader
        // holding both controls.
        //
        // ⚠ Filtered HERE, before the rows are built, so the Age counts and the "empty age" notes
        // describe what is on screen. Building all 47 and hiding some — the way the search filter
        // works — would leave every Age header claiming a count the reader cannot see.
        const focus = focusedTrack && allTracks.some((t) => t.timeline_id === focusedTrack) ? focusedTrack : null;
        const focusTrack = focus ? allTracks.find((t) => t.timeline_id === focus) ?? null : null;
        const focusScope = new Set<string>(focusTrack ? [focusTrack.timeline_id, ...focusTrack.descendant_ids] : []);

        const allVisible = visibleTracks(allTracks, collapsedTracks);
        const tracks = focus ? allVisible.filter((t) => focusScope.has(t.timeline_id)) : allVisible;
        const eras = data.eras || [];
        const allEntries = data.entries || [];
        const entries = focus
            ? allEntries.filter((e) => e.timeline_ids.some((id) => focusScope.has(id)))
            : allEntries;
        const canAuthor = !!data.can_author;

        // Author tools live in the page toolbar; the SERVER decides whether they appear.
        const authorTools = document.querySelector<HTMLElement>('[data-ft-timeline-author-tools]');
        if (authorTools && canAuthor) {
            authorTools.style.removeProperty('display');
        }

        wireEditors(data, allTracks, canAuthor);

        if (!tracks.length && !entries.length) {
            container!.appendChild(el('div', 'text-center text-muted py-5', emptyText));
            return;
        }

        // ⚠ SAY SO OUT LOUD. Focus removes rows from the page, and a chronology that is suddenly
        // a third as long — with no banner — reads as data loss, not as a filter. The counts are
        // named so nothing can go missing quietly, and the exit is one click.
        if (focusTrack) {
            const bar = el('div', 'ft-timeline-focusbar');
            bar.appendChild(el('span', 'ft-timeline-focusbar-eye')).innerHTML =
                typeof iconMap === 'function' ? iconMap('reveal', 16, 16, false) : '';
            const chip = el('span', 'ft-timeline-focusbar-chip', focusTrack.name);
            chip.style.setProperty('--ft-track-colour', trackColour(focusTrack, 0));
            bar.appendChild(el('span', 'ft-timeline-focusbar-label', 'Focused on'));
            bar.appendChild(chip);
            bar.appendChild(el('span', 'ft-timeline-focusbar-count',
                `${entries.length} of ${allEntries.length} entries`
                + (focusTrack.descendant_ids.length ? ', including everything under it' : '')));
            const clear = document.createElement('button');
            clear.type = 'button';
            clear.className = 'btn btn-sm btn-outline-light ft-timeline-focusbar-clear';
            clear.textContent = 'Show all tracks';
            clear.addEventListener('click', () => { focusedTrack = null; build(data); });
            bar.appendChild(clear);
            container!.appendChild(bar);
        }

        // Lane header — one column per track, sticky so the reader keeps their bearings while
        // scrolling three centuries.
        if (tracks.length) {
            const header = el('div', 'ft-timeline-header');
            // A LABEL in the date gutter. Without it the strip is a dark full-width bar with four
            // small chips huddled at the left — it reads as a rendering fault rather than as the
            // legend for the lanes below it.
            const headerLabel = el('div', 'ft-timeline-date');
            headerLabel.appendChild(el('span', 'ft-timeline-header-label', 'Tracks'));
            header.appendChild(headerLabel);
            const lanes = el('div', 'ft-timeline-lanes');
            lanes.style.setProperty('--ft-lane-count', String(Math.max(1, tracks.length)));
            // ── The chip strip (row 1) and the branch layer (row 2) ─────────────────────
            // The tree is drawn UNDERNEATH the chips rather than through them: a spine drops
            // out of the parent's column, runs right, and puts an ARROWHEAD down into each
            // child's column — landing exactly where that child's rail starts. Drawn level
            // with the chips it had to cross the parent's own label to reach the children.
            tracks.forEach((track, i) => {
                const cell = el('div', 'ft-timeline-lane ft-timeline-lane-head');
                cell.style.gridColumn = String(i + 1);
                // ⭐ ONE TIER PER DEPTH. A child chip sits BELOW the arrow that feeds it, so the
                // header reads top-down as a family tree: root chips, branch layer, child chips,
                // branch layer, grandchild chips. Depth d therefore owns grid row 2d+1 and the
                // branches into it own row 2d. Rows are implicit, so an eighth level costs nothing.
                cell.style.gridRow = String(track.depth * 2 + 1);

                // ⚠ The elbow SPANS THE GRID from the parent's own column to this one — it is
                // not drawn "one lane to the left". With three siblings under Zin, a
                // left-relative elbow made Lyela look like it branched off Orrin and Vagrax
                // off Lyela, i.e. a chain instead of a fan. Sibling elbows overlap along the
                // same spine, so what the reader sees is one trunk with a tick per child.
                if (track.depth > 0) {
                    cell.classList.add('ft-timeline-lane-head--nested');
                    const parentIndex = tracks.findIndex((t) => t.timeline_id === track.parent_id);
                    if (parentIndex >= 0 && parentIndex < i) {
                        const branch = el('div', 'ft-timeline-branch');
                        branch.style.gridColumn = `${parentIndex + 1} / ${i + 2}`;
                        branch.style.gridRow = String(track.depth * 2);
                        // The branch wears the PARENT's colour, so "this hangs off Zin" is
                        // readable without tracing the line back to its origin.
                        const parentTrack = tracks[parentIndex];
                        if (parentTrack) branch.style.setProperty('--ft-branch-colour', trackColour(parentTrack, parentIndex));
                        lanes.appendChild(branch);
                    }
                }

                // ⭐ THE CHIP IS THE FOLD BUTTON (owner ask 2026-08-21). The separate caret it
                // replaced was a second control inside a 36px lane, competing with the chip
                // directly above it for the same meaning. A folded chip wears a RING, so a
                // lane standing in for hidden children says so rather than looking ordinary.
                const foldable = track.descendant_ids.length > 0;
                const isCollapsed = collapsedTracks.has(track.timeline_id);
                const interactive = foldable || canAuthor;
                const chip = el(interactive ? 'button' : 'span', 'ft-timeline-track-chip', track.name);
                if (chip instanceof HTMLButtonElement) chip.type = 'button';
                chip.style.setProperty('--ft-track-colour', trackColour(track, i));
                if (interactive) chip.classList.add('ft-timeline-track-chip--editable');
                if (foldable) {
                    chip.classList.add('ft-timeline-track-chip--foldable');
                    if (isCollapsed) chip.classList.add('ft-timeline-track-chip--folded');
                    chip.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
                }
                if (interactive) {
                    chip.setAttribute('data-bs-toggle', 'tooltip');
                    chip.setAttribute('data-bs-original-title', trackChipTitle(track, isCollapsed, canAuthor));
                }

                const toggleFold = (): void => {
                    if (isCollapsed) collapsedTracks.delete(track.timeline_id);
                    else collapsedTracks.add(track.timeline_id);
                    // The lane set changes, so the whole chronology re-lays out. Rebuilt from
                    // the payload we already hold — no refetch.
                    build(data);
                };

                if (foldable && canAuthor) {
                    // ⚠ Both actions live on one control, and folding REBUILDS the header —
                    // which destroys this very element. Running the fold on the first click
                    // would therefore mean the dblclick never fires, so the fold is held for
                    // one double-click interval and cancelled if a second click arrives.
                    let pending: number | null = null;
                    chip.addEventListener('click', () => {
                        if (pending !== null) return;
                        pending = window.setTimeout(() => { pending = null; toggleFold(); }, 240);
                    });
                    chip.addEventListener('dblclick', (e) => {
                        e.preventDefault();
                        if (pending !== null) { window.clearTimeout(pending); pending = null; }
                        openTrackEditor(track);
                    });
                } else if (foldable) {
                    chip.addEventListener('click', toggleFold);
                } else if (canAuthor) {
                    chip.addEventListener('click', () => openTrackEditor(track));
                }

                cell.appendChild(chip);
                // ⭐ THE EYE — focus this track (owner design 2026-08-21). A real <button> beneath
                // the chip. `reveal` is the catalog's eye glyph; there is no eye-off to pair with
                // it — the icon named `hide` is a LEATHER HIDE — so the focused state is carried by
                // the button's own styling instead of a second mark.
                if (allTracks.length > 1) {
                    const eye = document.createElement('button');
                    eye.type = 'button';
                    eye.className = 'ft-timeline-lane-eye';
                    const isFocus = focus === track.timeline_id;
                    if (isFocus) eye.classList.add('ft-timeline-lane-eye--on');
                    eye.setAttribute('aria-pressed', isFocus ? 'true' : 'false');
                    eye.setAttribute('data-bs-toggle', 'tooltip');
                    eye.setAttribute('data-bs-original-title', isFocus
                        ? `Showing only ${track.name} — click to show every track`
                        : `Focus ${track.name}${track.descendant_ids.length ? ' and everything under it' : ''}`);
                    eye.innerHTML = typeof iconMap === 'function' ? iconMap('reveal', 14, 14, false) : '';
                    eye.addEventListener('click', (e) => {
                        e.stopPropagation();
                        focusedTrack = isFocus ? null : track.timeline_id;
                        build(data);
                    });
                    cell.appendChild(eye);
                }
                lanes.appendChild(cell);
            });
            header.appendChild(lanes);
            // Spacer for the medallion column, so the header's body cell lines up with the rows'.
            header.appendChild(el('div', 'ft-timeline-medallion-cell'));
            header.appendChild(el('div', 'ft-timeline-body'));
            container!.appendChild(header);
        }

        const built: BuiltEntry[] = [];
        const paintedEras = new Set<string>();
        let previousYear: number | null = null;

        // ── AGE SECTIONS ────────────────────────────────────────────────────────────────────
        // Each Age is its OWN <section> with the band as its header and its entries nested inside,
        // rather than everything being one flat run of sibling rows with bands interleaved
        // (owner ask 2026-08-21). Three things fall out of the nesting that the flat list could not
        // do: an Age can be COLLAPSED, its entry COUNT can be shown on the header, and an Age whose
        // entries are all filtered out can hide itself.
        const ageSections: { era: WorldTimelineEntry | null; section: HTMLElement; body: HTMLElement; rows: HTMLElement[] }[] = [];

        // Entries dated before the first Age still need somewhere to live — an unheaded section, so
        // nothing can ever silently fail to render.
        let current = (() => {
            const section = el('section', 'ft-timeline-age ft-timeline-age--unbanded');
            const body = el('div', 'ft-timeline-age-body');
            section.appendChild(body);
            const rec = { era: null as WorldTimelineEntry | null, section, body, rows: [] as HTMLElement[] };
            ageSections.push(rec);
            container!.appendChild(section);
            return rec;
        })();

        // Open every era whose start is at or before `key`, in order, once each.
        //
        // ⭐ WHY THIS IS NOT "the era the current entry falls in". Zin's first three Ages — Beginnings
        // and Endings, Titans, The Birthing of Gods — contain NO events at all: the earliest dated
        // entry is 31 BT, inside Age 4. Opening a section only when an entry lands in it therefore
        // drops three of the seven Ages off the page entirely, and a reader comparing against doc 05
        // finds the cosmology simply missing. An Age is a claim about the shape of history, so it
        // belongs on the page whether or not anyone has written events into it yet.
        const paintErasUpTo = (key: number | null): void => {
            for (const era of eras) {
                if (paintedEras.has(era.event_id)) continue;
                const from = era.start_sort_key;
                // An open-start era (Age 1) leads the chronology; a dated one opens once the reader
                // has reached its start.
                const due = era.open_start || from === null || key === null || key >= from;
                if (!due) continue;

                const section = el('section', 'ft-timeline-age');
                const body = el('div', 'ft-timeline-age-body');
                section.appendChild(buildEraBand(era, body, canAuthor, openEntryEditor));
                section.appendChild(body);
                container!.appendChild(section);

                current = { era, section, body, rows: [] };
                ageSections.push(current);
                paintedEras.add(era.event_id);
                previousYear = null;   // a fresh Age is not "a gap" — the header already says so
            }
        };

        // The marker is inserted at its chronological position, not pinned to the end — the whole
        // value of "you are here" is seeing what the party has already lived through.
        const marker = data.marker || null;
        let markerPlaced = !marker;

        // ── Which years are dense enough to fold? ────────────────────────────────────────────
        // Counted up front over the WHOLE chronology, because the decision is per-year and the
        // loop below meets a year's entries one at a time.
        const pendingClusters: Array<{ key: string; rows: HTMLElement[]; body: HTMLElement }> = [];
        const perYear = new Map<number, number>();
        for (const e of entries) {
            if (e.start_sort_year === null) { continue; }
            perYear.set(e.start_sort_year, (perYear.get(e.start_sort_year) || 0) + 1);
        }
        const seenInYear = new Map<number, number>();

        for (const entry of entries) {

            if (marker && !markerPlaced && entry.start_sort_key !== null
                && marker.sort_key !== null && entry.start_sort_key > marker.sort_key) {
                current.body.appendChild(buildMarker(marker));
                markerPlaced = true;
            }

            // Open every Age the chronology has now reached, including any with no entries of
            // their own, so all seven of Zin's Ages appear in order.
            paintErasUpTo(entry.start_sort_key);

            // The 159–290 AT silence and its kin. Measured from the previous entry's END where it
            // has one: the Tomb of Aksirma runs 154–159 AT, so the silence before the War of Sin
            // is the doc's 131 years, not the 136 you get by measuring from where the tomb STARTED.
            const year = entry.start_sort_year;
            if (previousYear !== null && year !== null && year - previousYear >= GAP_YEARS) {
                current.body.appendChild(buildGapMarker(previousYear, year));
            }
            const reachedYear = entry.end_sort_year ?? entry.start_sort_year;
            if (reachedYear !== null) {
                previousYear = previousYear === null ? reachedYear : Math.max(previousYear, reachedYear);
            }

            const row = buildEntryRow(entry, tracks, canAuthor, openEntryEditor, collapsedTracks);

            // ── Fold a dense year ────────────────────────────────────────────────────────────
            const y = entry.start_sort_year;
            const yearCount = y === null ? 0 : (perYear.get(y) || 0);
            if (y !== null && yearCount >= CLUSTER_MIN) {
                const idx = (seenInYear.get(y) || 0) + 1;
                seenInYear.set(y, idx);
                const key = `y${y}`;
                row.setAttribute('data-ft-cluster', key);

                if (idx > CLUSTER_PREVIEW) {
                    row.classList.add('ft-timeline-clustered-hidden');
                }
                current.body.appendChild(row);

                // The toggle goes in once, immediately after the last previewed row, so it reads as
                // "…and there is more of this year" rather than floating at the section's end.
                if (idx === CLUSTER_PREVIEW) {
                    const label = entry.label || (y < 0 ? `${Math.abs(y)} BT` : `${y} AT`);
                    const hidden = yearCount - CLUSTER_PREVIEW;
                    const rowsForYear: HTMLElement[] = [];
                    const toggle = buildClusterToggle(label.replace(/,.*$/, ''), hidden, rowsForYear);
                    toggle.setAttribute('data-ft-cluster-toggle', key);
                    current.body.appendChild(toggle);
                    // Filled after the loop — the later rows do not exist yet.
                    pendingClusters.push({ key, rows: rowsForYear, body: current.body });
                }
            } else {
                current.body.appendChild(row);
            }

            current.rows.push(row);
            built.push({ el: row, text: entry.text || entry.name.toLowerCase(), entry });
        }

        // Hand each toggle the rows it controls, now that every row exists.
        pendingClusters.forEach((c) => {
            c.body.querySelectorAll<HTMLElement>(`[data-ft-cluster="${c.key}"].ft-timeline-clustered-hidden`)
                .forEach((r) => c.rows.push(r));
        });

        // Any Age later than the last recorded entry still gets its section — an era that has begun
        // but has nothing written in it yet is exactly the state new canon gets authored into.
        paintErasUpTo(null);

        if (marker && !markerPlaced) {
            current.body.appendChild(buildMarker(marker));
        }

        // Entry counts on each Age header, and the empty ones said out loud. "Nothing recorded yet"
        // is INFORMATION on a world chronicle — it is where the next canon goes — so an empty Age
        // says so rather than rendering as a header with a void beneath it.
        for (const rec of ageSections) {
            if (!rec.era) {
                // The pre-first-Age bucket. If nothing landed in it, it should not exist at all.
                if (!rec.rows.length) rec.section.remove();
                continue;
            }
            const count = el('span', 'ft-timeline-age-count', rec.rows.length === 1 ? '1 event' : `${rec.rows.length} events`);
            rec.section.querySelector('.ft-timeline-era-meta')?.appendChild(count);
            if (!rec.rows.length) {
                rec.section.classList.add('ft-timeline-age--empty');
                rec.body.appendChild(el('div', 'ft-timeline-age-empty', 'Nothing recorded in this age yet.'));
            }
        }

        // The Age rail goes in LAST — it measures the sections, so they all have to exist and be
        // populated first. It is appended to the container (not the body) so it can position itself
        // against the timeline's own box rather than the page's.
        const rail = buildAgeRail(container!, ageSections);
        if (rail) { container!.appendChild(rail); }

        const emptyEl = el('div', 'text-center text-muted py-5', emptyText);
        emptyEl.style.display = built.length ? 'none' : '';
        container!.appendChild(emptyEl);

        initTooltips(container!);

        // ── Filtering: toggle visibility, never rebuild and NEVER reorder ──────────────────
        const applyFilter = (): void => {
            const keyword = (searchInput?.value || '').trim().toLowerCase();
            const hideCampaign = !!campaignToggle?.checked;
            let visible = 0;
            for (const b of built) {
                const tierOk = !hideCampaign || b.entry.canon_tier !== 'campaign';
                const show = tierOk && (!keyword || b.text.includes(keyword)
                    || (b.entry.description || '').toLowerCase().includes(keyword));
                b.el.style.display = show ? '' : 'none';
                // ⛔ A hit inside a COLLAPSED cluster must force it open, or the result is invisible
                // and the search silently under-reports. Folding is a reading convenience; it must
                // never hide a match.
                if (show && keyword) { revealCluster(b.el); }
                if (show) visible++;
            }
            // Gap markers, cluster toggles and the "nothing recorded yet" notes are chrome for the
            // entries around them. Under a search they'd float free of anything they describe, so
            // they step aside.
            container!.querySelectorAll<HTMLElement>('.ft-timeline-gap, .ft-timeline-age-empty, .ft-timeline-cluster-toggle')
                .forEach((node) => { node.style.display = keyword ? 'none' : ''; });
            // Clearing the search restores the fold, so a long year does not stay blown open.
            if (!keyword) {
                container!.querySelectorAll<HTMLElement>('[data-ft-cluster-toggle]').forEach((t) => {
                    if (t.getAttribute('aria-expanded') !== 'true') { return; }
                    t.setAttribute('aria-expanded', 'false');
                    const key = t.getAttribute('data-ft-cluster-toggle');
                    const rows = container!.querySelectorAll<HTMLElement>(`[data-ft-cluster="${key}"]`);
                    rows.forEach((r, i) => {
                        if (i >= CLUSTER_PREVIEW) { r.classList.add('ft-timeline-clustered-hidden'); }
                    });
                    t.textContent = `${Math.max(0, rows.length - CLUSTER_PREVIEW)} more`;
                });
            }

            // An AGE, though, stays as long as it still has a visible entry — the Age is the thing
            // that tells you WHEN a hit happened, so hiding all the headers during a search (which
            // the flat layout had to do) stripped every result of its context. An Age with no
            // surviving entries hides itself entirely.
            for (const rec of ageSections) {
                if (!rec.era) continue;
                const anyVisible = rec.rows.some((r) => r.style.display !== 'none');
                rec.section.style.display = (!keyword || anyVisible) ? '' : 'none';
                // A collapsed Age must re-open to show a hit, or the result is invisible.
                if (keyword && anyVisible) {
                    const toggle = rec.section.querySelector<HTMLElement>('.ft-timeline-era-toggle');
                    if (toggle?.getAttribute('aria-expanded') === 'false') toggle.click();
                }
            }

            emptyEl.textContent = keyword ? 'Nothing in this history matches your search.' : emptyText;
            emptyEl.style.display = visible === 0 ? '' : 'none';
        };

        // ── Jump to year (2026-08-23) ────────────────────────────────────────────────────
        // Offered only when there is enough history to get lost in.
        const jumpWrap = document.querySelector<HTMLElement>('[data-ft-timeline-jump]');
        const jumpInput = document.getElementById('ft_wt_year') as HTMLInputElement | null;
        if (jumpWrap && jumpInput) {
            jumpWrap.hidden = built.length < 12;
            const status = document.getElementById('ft_wt_year_status');
            // `.is-invalid` is a COLOUR. On its own it tells a screen-reader user nothing, and it
            // tells a sighted user nothing about WHY. aria-invalid carries the state and the live
            // region carries the reason - including the case where the jump finds nothing to land on.
            const say = (msg: string, bad: boolean): void => {
                jumpInput.classList.toggle('is-invalid', bad);
                jumpInput.setAttribute('aria-invalid', bad ? 'true' : 'false');
                if (status) { status.textContent = msg; }
            };
            const go = (): void => {
                const target = parseJumpYear(jumpInput.value);
                if (target === null) {
                    say('Not a year. Try 431, 431 AT or 31 BT.', true);
                    return;
                }
                const landed = jumpToYear(built, target);
                if (!landed) {
                    say('Nothing to jump to - every entry is hidden by the current filters.', true);
                    return;
                }
                const name = landed.querySelector('.ft-timeline-title')?.textContent || 'entry';
                const when = landed.querySelector('.ft-timeline-date-label')?.textContent || '';
                say('Jumped to ' + name + (when ? ', ' + when : '') + '.', false);
            };
            // Enter is invisible. A button is the only thing that says the control does anything.
            const goBtn = document.getElementById('ft_wt_year_go') as HTMLButtonElement | null;
            if (goBtn) { goBtn.onclick = go; }
            jumpInput.onkeydown = (ev: KeyboardEvent) => {
                if (ev.key !== 'Enter') { return; }
                // ⛔ The input sits in the page chrome; Enter must not submit anything around it.
                ev.preventDefault();
                go();
            };
            jumpInput.oninput = () => {
                jumpInput.classList.remove('is-invalid');
                jumpInput.setAttribute('aria-invalid', 'false');
            };
        }

        // ── Age nav for narrow viewports (2026-08-23) ────────────────────────────────────
        // The Age rail is display:none below 1100px, which left narrow screens with no navigation
        // at all. Same destinations, as a native select; the CSS hides it once the rail returns.
        const ageNavWrap = document.querySelector<HTMLElement>('[data-ft-timeline-agenav]');
        const ageNav = document.getElementById('ft_wt_age') as HTMLSelectElement | null;
        if (ageNavWrap && ageNav) {
            const navAges = ageSections.filter((r) => r.era && r.section.isConnected);
            ageNavWrap.hidden = navAges.length < 2;
            ageNav.innerHTML = '';
            const head = document.createElement('option');
            head.value = ''; head.textContent = 'Jump to an Age…';
            ageNav.appendChild(head);
            navAges.forEach((rec, i) => {
                const o = document.createElement('option');
                o.value = String(i);
                o.textContent = `${rec.era?.name || 'Age'} (${rec.rows.length})`;
                ageNav.appendChild(o);
            });
            ageNav.onchange = () => {
                const rec = navAges[Number(ageNav.value)];
                if (!rec) { return; }
                rec.section.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
                const heading = rec.section.querySelector<HTMLElement>('.ft-timeline-era-title')
                    || rec.section.querySelector<HTMLElement>('h2, h3')
                    || rec.section;
                if (!heading.hasAttribute('tabindex')) { heading.setAttribute('tabindex', '-1'); }
                heading.focus({ preventScroll: true });
                ageNav.value = '';   // reset, so the same Age can be chosen twice running
            };
        }

        // The campaign fold — only offered when there is something to fold.
        if (campaignToggle) {
            const wrap = campaignToggle.closest<HTMLElement>('[data-ft-timeline-campaign-toggle]');
            const any = built.some((b) => b.entry.canon_tier === 'campaign');
            if (wrap) { wrap.hidden = !any; }
            // Property slot, not addEventListener: each render rebinds to ITS applyFilter instead of
            // stacking handlers that close over stale row collections.
            campaignToggle.onchange = applyFilter;
        }
        if (searchInput) {
            const box = searchInput.closest('.ft-inline-search-root');
            const glyph = box?.querySelector('[data-ft-inline-search-role="glyph"]') ?? null;
            const clear = box?.querySelector('[data-ft-inline-search-role="clear"]') ?? null;
            const syncTrailing = (): void => {
                if (!glyph || !clear) return;
                const hasText = (searchInput.value || '').trim().length > 0;
                glyph.classList.toggle('d-none', hasText);
                clear.classList.toggle('d-none', !hasText);
            };
            searchInput.addEventListener('input', () => { syncTrailing(); applyFilter(); });
            if (clear) {
                clear.addEventListener('click', (e) => {
                    e.preventDefault();
                    searchInput.value = '';
                    searchInput.focus();
                    syncTrailing();
                    applyFilter();
                });
            }
            syncTrailing();
        }

        applyFilter();
    }

    // ── Editors ─────────────────────────────────────────────────────────────────────────────
    // Folded parent tracks, by id. Mount-scoped so a fold survives the rebuild that folding
    // itself triggers — the lane set changes, so the chronology has to re-lay out.
    const collapsedTracks = new Set<string>();
    // The eye: which track the reader has zoomed into, or null for the whole chronology.
    let focusedTrack: string | null = null;
    // ⚠ DEEP SUBTREES START FOLDED, seeded ONCE from the first payload. The header is a sticky
    // tier per depth, so a four-level tree renders a ~400px legend before the reader reaches a
    // single event — and it grows with every level. Folding anything that would push past the
    // second tier keeps the default view two tiers deep however deep the tree actually goes; the
    // reader opens the branch they want. ⚠ Seeded once, or every rebuild would re-fold what the
    // reader just opened.
    let deepFoldsSeeded = false;
    const seedDeepFolds = (tracks: WorldTimelineTrack[]): void => {
        if (deepFoldsSeeded || !tracks.length) return;
        deepFoldsSeeded = true;
        for (const t of tracks) {
            if (t.depth >= 1 && t.descendant_ids.length) collapsedTracks.add(t.timeline_id);
        }
    };

    let openEntryEditor: (entry: WorldTimelineEntry | null, kind?: string) => void = () => {};
    let openTrackEditor: (track: WorldTimelineTrack | null) => void = () => {};
    // Seeds the category tiles when a panel opens; assigned by wireEditors().
    let syncCategoryPicker: (value: string) => void = () => {};

    function wireEditors(
        data: CatalogWorldTimelineResponse,
        tracks: WorldTimelineTrack[],
        canAuthor: boolean,
    ): void {
        if (!canAuthor) return;

        const eventPanel = document.getElementById('ft_timeline_event_editor');
        const trackPanel = document.getElementById('ft_timeline_track_editor');
        const eventForm = eventPanel?.querySelector<HTMLElement>('[data-ft-timeline-event-form]') || null;
        const trackForm = trackPanel?.querySelector<HTMLElement>('[data-ft-timeline-track-form]') || null;

        const show = (panel: HTMLElement | null): void => {
            if (!panel || typeof bootstrap === 'undefined' || !bootstrap?.Offcanvas) return;
            bootstrap.Offcanvas.getOrCreateInstance(panel).show();
        };
        const hide = (panel: HTMLElement | null): void => {
            if (!panel || typeof bootstrap === 'undefined' || !bootstrap?.Offcanvas) return;
            bootstrap.Offcanvas.getOrCreateInstance(panel).hide();
        };

        // ── CATEGORY PICKER ─────────────────────────────────────────────────────────────
        // The category decides the MEDALLION, so the control shows the mark being chosen. A
        // <select> cannot render one; the datalist this replaced looked like a closed list but
        // silently accepted anything, so an off-list value fell back to the generic pin with no
        // hint until the author looked at the timeline.
        //
        // The stored column is still a free varchar (§3d). Three parts keep that honest:
        //   · the tile grid writes a known slug        (the obvious path)
        //   · the Custom box writes anything           (the escape hatch, still supported)
        //   · a value in neither leaves NO tile selected and shows in Custom — so an author
        //     always sees which of the two they are in.
        const catPicker = document.querySelector<HTMLElement>('[data-ft-timeline-catpicker]');
        const catHidden = document.getElementById('ft_te_category') as HTMLInputElement | null;
        const catCustom = document.getElementById('ft_te_category_custom') as HTMLInputElement | null;
        const categories = data.categories || [];

        const syncCatSelection = (value: string): void => {
            const known = categories.some((c) => c.slug === value);
            catPicker?.querySelectorAll<HTMLElement>('.ft-timeline-cattile').forEach((tile) => {
                tile.classList.toggle('is-selected', tile.dataset.slug === value);
                tile.setAttribute('aria-pressed', tile.dataset.slug === value ? 'true' : 'false');
            });
            if (catCustom) catCustom.value = known ? '' : value;
        };

        if (catPicker && !catPicker.hasAttribute('data-ft-built')) {
            catPicker.setAttribute('data-ft-built', '1');
            catPicker.innerHTML = '';
            for (const cat of categories) {
                const tile = document.createElement('button');
                tile.type = 'button';
                tile.className = 'ft-timeline-cattile';
                tile.dataset.slug = cat.slug;
                tile.style.setProperty('--ft-cat-colour', cat.colour || '#8fa3b8');
                tile.setAttribute('aria-pressed', 'false');
                const mark = el('span', 'ft-timeline-cattile-mark');
                mark.innerHTML = icon(cat.icon, 22, 22, false);
                tile.appendChild(mark);
                tile.appendChild(el('span', 'ft-timeline-cattile-label', cat.label));
                tile.addEventListener('click', () => {
                    // Clicking the selected tile clears it — otherwise there is no way back to
                    // "no category" once one is picked.
                    const next = catHidden?.value === cat.slug ? '' : cat.slug;
                    if (catHidden) catHidden.value = next;
                    syncCatSelection(next);
                });
                catPicker.appendChild(tile);
            }
        }

        // Typing a custom value clears any tile; typing a known slug selects its tile.
        if (catCustom && !catCustom.hasAttribute('data-ft-wired')) {
            catCustom.setAttribute('data-ft-wired', '1');
            catCustom.addEventListener('input', () => {
                if (catHidden) catHidden.value = catCustom.value.trim();
                catPicker?.querySelectorAll<HTMLElement>('.ft-timeline-cattile').forEach((tile) => {
                    const hit = tile.dataset.slug === catCustom.value.trim();
                    tile.classList.toggle('is-selected', hit);
                    tile.setAttribute('aria-pressed', hit ? 'true' : 'false');
                });
            });
        }

        // ⭐ The campaign picker (author-only data) and the tier → picker reveal.
        const tierSelect = document.getElementById('ft_te_canon_tier') as HTMLSelectElement | null;
        const campaignSelect = document.getElementById('ft_te_campaign_id') as HTMLSelectElement | null;
        const campaignWrap = document.getElementById('ft_te_campaign_wrap');
        if (campaignSelect) {
            const keep = campaignSelect.value;
            campaignSelect.innerHTML = '';
            const none = document.createElement('option');
            none.value = ''; none.textContent = '— none named —';
            campaignSelect.appendChild(none);
            for (const c of data.campaigns || []) {
                const option = document.createElement('option');
                option.value = c.campaign_id; option.textContent = c.name;
                campaignSelect.appendChild(option);
            }
            campaignSelect.value = keep;
        }
        const syncTierUi = (): void => {
            if (campaignWrap) { campaignWrap.hidden = tierSelect?.value !== 'campaign'; }
        };
        if (tierSelect && !tierSelect.dataset.ftBound) {
            tierSelect.dataset.ftBound = '1';
            tierSelect.addEventListener('change', syncTierUi);
            // writeForm() sets values silently; re-sync whenever the editor opens.
            eventForm?.addEventListener('ft:form-written', syncTierUi);
        }

        // Still populate the datalist — it now serves the Custom box, where suggestions still help.
        const catOptions = document.getElementById('ft_te_category_options');
        if (catOptions) {
            catOptions.innerHTML = '';
            for (const cat of categories) {
                const option = document.createElement('option');
                option.value = cat.slug;
                option.textContent = cat.label;
                catOptions.appendChild(option);
            }
        }

        // Exposed so openEntryEditor() can seed the picker when a panel opens.
        syncCategoryPicker = syncCatSelection;

        // Month suggestions from the world's calendar, when it has one. A datalist, NOT a select:
        // the month count is world-data, so the UI offers what the calendar says and still accepts
        // anything (handoff §3e).
        const monthOptions = document.getElementById('ft_te_month_options');
        if (monthOptions) {
            monthOptions.innerHTML = '';
            for (const month of data.calendar?.months || []) {
                const option = document.createElement('option');
                option.value = String(month.order);
                option.textContent = month.name ? `${month.order} — ${month.name}` : String(month.order);
                monthOptions.appendChild(option);
            }
        }

        // ── ENTRY editor ────────────────────────────────────────────────────────────────
        openEntryEditor = (entry: WorldTimelineEntry | null, kind = 'event'): void => {
            if (!eventForm) return;
            showEditorError(eventForm, '');

            const isEra = (entry?.kind || kind) === 'era';
            dressEditor(
                'ft_timeline_event_editor',
                entry?.category_colour || (isEra ? '#8e7bd6' : '#22d3ee'),
                entry?.category_icon || (isEra ? 'create_timeline_age' : 'create_timeline_event'),
                entry
                    ? (entry.derived ? 'Make this a full entry' : (isEra ? 'Edit age' : 'Edit entry'))
                    : (isEra ? 'New age' : 'New event'),
            );

            writeForm(eventForm, {
                // A derived entry's synthetic id IS sent back — the write endpoint reads
                // "loc:<uuid>:founding" as a promotion, minting a real row that claims that
                // location date so the projection stops emitting it.
                timeline_event_id: entry?.event_id || '',
                timeline_event_kind: entry?.kind || kind,
                timeline_event_name: entry?.name || '',
                timeline_event_description: entry?.description || '',
                timeline_event_category: entry?.category || '',
                timeline_event_gm_notes: entry?.gm_notes || '',
                timeline_event_private: entry ? String(entry.is_private) : '0',
                timeline_event_canon_tier: entry?.canon_tier || 'default',
                timeline_event_campaign_id: entry?.campaign_id || '',
                timeline_event_start_year: entry?.start.year ?? '',
                timeline_event_start_month: entry?.start.month ?? '',
                timeline_event_start_day: entry?.start.day ?? '',
                timeline_event_start_era: entry?.start.era || 'AT',
                timeline_event_start_minute: entry?.start.minute ?? null,
                timeline_event_end_year: entry?.end.year ?? '',
                timeline_event_end_month: entry?.end.month ?? '',
                timeline_event_end_day: entry?.end.day ?? '',
                timeline_event_end_era: entry?.end.era || '',
                timeline_event_end_minute: entry?.end.minute ?? null,
                timeline_event_open_start: entry?.open_start ? 1 : 0,
                timeline_event_open_end: entry?.open_end ? 1 : 0,
                timeline_event_start_is_approximate: entry?.start.approximate ? 1 : 0,
                timeline_event_end_is_approximate: entry?.end.approximate ? 1 : 0,
            });

            // Seed the category tiles from whatever the hidden field now holds. A new ERA defaults
            // to 'age' so its medallion matches the Age bands rather than opening uncategorised.
            const seededCategory = entry?.category || (entry ? '' : (isEra ? 'age' : ''));
            const catField = document.getElementById('ft_te_category') as HTMLInputElement | null;
            if (catField) catField.value = seededCategory;
            syncCategoryPicker(seededCategory);

            // ── AGE BANNER PICKER ───────────────────────────────────────────────────────────
            // Only an Age gets one: an entry row has no space for cover art, and offering the
            // control there would be a promise the renderer cannot keep.
            const bannerSet = eventForm.querySelector<HTMLElement>('[data-ft-timeline-banner-fieldset]');
            const bannerBox = eventForm.querySelector<HTMLElement>('[data-ft-timeline-bannerpicker]');
            const bannerEmpty = eventForm.querySelector<HTMLElement>('[data-ft-timeline-banner-empty]');
            const bannerField = eventForm.querySelector<HTMLInputElement>('[data-ft-field="timeline_event_banner_picture_id"]');
            if (bannerSet) bannerSet.style.display = isEra ? '' : 'none';
            if (isEra && bannerBox && bannerField) {
                // ⭐ THE AGE'S OWN UPLOADS FIRST, then the world's art as reusable alternatives
                // (owner ruling 2026-08-21). Deduped by id, because a world picture is not also an
                // entry picture but a future source might overlap.
                const own = (entry && data.entry_pictures?.[entry.event_id]) || [];
                const seenPics = new Set(own.map((p) => p.picture_id));
                const pictures = [...own, ...(data.world_pictures || []).filter((p) => !seenPics.has(p.picture_id))];
                bannerField.value = entry?.banner_picture_id || '';
                if (bannerEmpty) bannerEmpty.style.display = pictures.length ? 'none' : '';
                bannerBox.innerHTML = '';

                const paint = (): void => {
                    for (const tile of Array.from(bannerBox.children)) {
                        const id = (tile as HTMLElement).dataset.pictureId || '';
                        tile.classList.toggle('ft-timeline-bannertile--on', id === bannerField.value);
                    }
                };

                // "None" first, so clearing a banner is as easy as setting one. Without it the only
                // way back from a picked banner is to reload the panel.
                const none = document.createElement('button');
                none.type = 'button';
                none.className = 'ft-timeline-bannertile ft-timeline-bannertile--none';
                none.dataset.pictureId = '';
                none.textContent = 'No banner';
                none.addEventListener('click', () => { bannerField.value = ''; paint(); });
                bannerBox.appendChild(none);

                for (const pic of pictures) {
                    const tile = document.createElement('button');
                    tile.type = 'button';
                    tile.className = 'ft-timeline-bannertile';
                    tile.dataset.pictureId = pic.picture_id;
                    tile.setAttribute('aria-label', pic.name || 'Picture');
                    const img = document.createElement('img');
                    img.src = pic.thumbnail || pic.url;
                    img.alt = '';
                    img.loading = 'lazy';
                    tile.appendChild(img);
                    // ⚠ SAY IT OUT LOUD. The Age band is on the PUBLIC page, so picking a private
                    // picture publishes it. That should be a deliberate act, not a surprise found
                    // later.
                    if (pic.is_private) {
                        tile.appendChild(el('span', 'ft-timeline-bannertile-private', 'private'));
                        tile.setAttribute('data-bs-toggle', 'tooltip');
                        tile.setAttribute('data-bs-original-title',
                            'This picture is private — using it as a banner shows it on the public timeline');
                    }
                    tile.addEventListener('click', () => { bannerField.value = pic.picture_id; paint(); });
                    bannerBox.appendChild(tile);
                }
                paint();
                initTooltips(bannerBox);

                // ⚠ UPLOAD IS ONLY REACHABLE ON A SAVED AGE. The media manager attaches pictures to
                // an entry id, and a brand-new Age does not have one yet — so the button says why
                // instead of opening a page that would 404 on an empty id.
                const upload = eventForm.querySelector<HTMLAnchorElement>('[data-ft-timeline-banner-upload]');
                const uploadHint = eventForm.querySelector<HTMLElement>('[data-ft-timeline-banner-upload-hint]');
                const base = data.media_manager_base || '';
                const canUpload = !!(entry && !entry.derived && base);
                if (upload) {
                    upload.style.display = canUpload ? '' : 'none';
                    if (canUpload) upload.href = base + encodeURIComponent(entry!.event_id);
                }
                if (uploadHint) uploadHint.style.display = canUpload ? 'none' : '';
            }

            // §3c membership checkboxes — the control that makes one event span four continents.
            const checks = eventForm.querySelector<HTMLElement>('[data-ft-timeline-track-checks]');
            if (checks) {
                checks.innerHTML = '';
                for (const track of tracks) {
                    const wrap = el('div', 'form-check');
                    const input = document.createElement('input');
                    input.className = 'form-check-input';
                    input.type = 'checkbox';
                    input.value = track.timeline_id;
                    input.id = `ft_te_track_${track.timeline_id}`;
                    input.checked = entry ? entry.timeline_ids.includes(track.timeline_id) : false;
                    const label = document.createElement('label');
                    label.className = 'form-check-label';
                    label.htmlFor = input.id;
                    label.textContent = track.name;
                    wrap.appendChild(input);
                    wrap.appendChild(label);
                    checks.appendChild(wrap);
                }
            }

            const deleteBtn = eventForm.querySelector<HTMLElement>('[data-ft-timeline-delete]');
            if (deleteBtn) {
                // A derived entry has no row to delete — it lives on the location.
                deleteBtn.style.display = entry && !entry.derived ? '' : 'none';
            }

            show(eventPanel);
        };

        if (eventForm && !eventForm.hasAttribute('data-ft-wired')) {
            eventForm.setAttribute('data-ft-wired', '1');

            eventForm.addEventListener('submit', (e) => {
                e.preventDefault();
                const payload = readForm(eventForm);
                payload.world_location_id = worldLocationId;
                payload.timeline_ids = Array.from(
                    eventForm.querySelectorAll<HTMLInputElement>('[data-ft-timeline-track-checks] input:checked'),
                ).map((input) => input.value);

                void (async () => {
                    try {
                        await postJson('options/tools/timelines/modify_timeline_event_inc.php', payload);
                        hide(eventPanel);
                        load();
                    } catch (err) {
                        showEditorError(eventForm, (err as Error).message || 'Could not save.');
                    }
                })();
            });

            const deleteBtn = eventForm.querySelector<HTMLElement>('[data-ft-timeline-delete]');
            deleteBtn?.addEventListener('click', () => {
                const payload = readForm(eventForm);
                if (!payload.timeline_event_id) return;
                if (!window.confirm('Delete this entry from the world’s history?')) return;
                void (async () => {
                    try {
                        await postJson('options/tools/timelines/modify_timeline_event_inc.php', {
                            timeline_event_id: payload.timeline_event_id,
                            world_location_id: worldLocationId,
                            delete: 1,
                        });
                        hide(eventPanel);
                        load();
                    } catch (err) {
                        showEditorError(eventForm, (err as Error).message || 'Could not delete.');
                    }
                })();
            });
        }

        // ── TRACK editor ────────────────────────────────────────────────────────────────
        openTrackEditor = (track: WorldTimelineTrack | null): void => {
            if (!trackForm) return;
            showEditorError(trackForm, '');

            dressEditor(
                'ft_timeline_track_editor',
                track ? trackColour(track, Math.max(0, tracks.indexOf(track))) : '#22d3ee',
                'create_world',
                track ? 'Edit track' : 'New track',
            );

            // Parent options: this world's other tracks. Self is excluded so a track can't be
            // made its own ancestor (the server rejects it too, but offering it is a trap).
            const parentSelect = document.getElementById('ft_tl_parent') as HTMLSelectElement | null;
            if (parentSelect) {
                parentSelect.innerHTML = '';
                const none = document.createElement('option');
                none.value = '';
                none.textContent = '— none (this track carries its own ages) —';
                parentSelect.appendChild(none);
                for (const other of tracks) {
                    if (track && other.timeline_id === track.timeline_id) continue;
                    const option = document.createElement('option');
                    option.value = other.timeline_id;
                    option.textContent = other.name;
                    parentSelect.appendChild(option);
                }
            }

            writeForm(trackForm, {
                timeline_id: track?.timeline_id || '',
                timeline_name: track?.name || '',
                timeline_description: track?.description || '',
                timeline_colour: /^#[0-9a-fA-F]{6}$/.test(track?.colour || '') ? track!.colour : '#4fc3f7',
                timeline_sort_order: track ? String(track.sort_order) : String(tracks.length),
                timeline_parent_id: track?.parent_id || '',
                timeline_private: track ? String(track.is_private) : '0',
            });

            const includeLocations = document.getElementById('ft_tl_include_locations') as HTMLInputElement | null;
            if (includeLocations) {
                // The real stored flag; a NEW track defaults on, matching the column default.
                includeLocations.checked = track ? !!track.include_location_dates : true;
            }

            const deleteBtn = trackForm.querySelector<HTMLElement>('[data-ft-timeline-delete]');
            if (deleteBtn) deleteBtn.style.display = track ? '' : 'none';

            show(trackPanel);
        };

        if (trackForm && !trackForm.hasAttribute('data-ft-wired')) {
            trackForm.setAttribute('data-ft-wired', '1');

            trackForm.addEventListener('submit', (e) => {
                e.preventDefault();
                const payload = readForm(trackForm);
                payload.world_location_id = worldLocationId;
                void (async () => {
                    try {
                        await postJson('options/tools/timelines/modify_timeline_inc.php', payload);
                        hide(trackPanel);
                        load();
                    } catch (err) {
                        showEditorError(trackForm, (err as Error).message || 'Could not save.');
                    }
                })();
            });

            const deleteBtn = trackForm.querySelector<HTMLElement>('[data-ft-timeline-delete]');
            deleteBtn?.addEventListener('click', () => {
                const payload = readForm(trackForm);
                if (!payload.timeline_id) return;
                if (!window.confirm('Remove this track? Its events stay in the world’s history.')) return;
                void (async () => {
                    try {
                        await postJson('options/tools/timelines/modify_timeline_inc.php', {
                            timeline_id: payload.timeline_id,
                            delete: 1,
                        });
                        hide(trackPanel);
                        load();
                    } catch (err) {
                        showEditorError(trackForm, (err as Error).message || 'Could not delete.');
                    }
                })();
            });
        }

        // Toolbar buttons.
        const newEvent = document.querySelector<HTMLElement>('[data-ft-timeline-new-event]');
        const newEra = document.querySelector<HTMLElement>('[data-ft-timeline-new-era]');
        const newTrack = document.querySelector<HTMLElement>('[data-ft-timeline-new-track]');
        if (newEvent && !newEvent.hasAttribute('data-ft-wired')) {
            newEvent.setAttribute('data-ft-wired', '1');
            newEvent.addEventListener('click', () => openEntryEditor(null, 'event'));
        }
        if (newEra && !newEra.hasAttribute('data-ft-wired')) {
            newEra.setAttribute('data-ft-wired', '1');
            newEra.addEventListener('click', () => openEntryEditor(null, 'era'));
        }
        if (newTrack && !newTrack.hasAttribute('data-ft-wired')) {
            newTrack.setAttribute('data-ft-wired', '1');
            newTrack.addEventListener('click', () => openTrackEditor(null));
        }
    }

    load();
}
